import React, {useEffect, useState} from 'react';
import FloatingImageControlBar from './FloatingImageControlBar';
import {useMergedScene, useTemplate} from "../../../../context/TemplateContext";
import {stepZoom} from "../../../../scene/sceneToKonva";


interface ImageControlWrapperProps {
    handleTransformClick: (key: string | undefined) => void;
    handleCloseClick: () => void;
    handleResetClick: () => void;
    selectedDesign: number;
}

/** Depth-first find of the scene image element bound to `inputKey` (collage cells
 *  can sit nested inside groups, so a flat scan isn't enough). */
const findImageElement = (elements: any[] | undefined, inputKey: string): any => {
    for (const el of elements || []) {
        if (el?.type === 'image' && el?.inputKey === inputKey) return el;
        const nested = findImageElement(el?.elements, inputKey);
        if (nested) return nested;
    }
    return undefined;
};

/**
 * Wrapper component to manage the image control bar state and functionality
 */
const FloatingImageControlBarWrapper: React.FC<ImageControlWrapperProps> = ({
                                                                     handleTransformClick,
                                                                     handleCloseClick,
                                                                     handleResetClick,
                                                                                selectedDesign
                                                                 }) => {
    const {
        selectedImageData,
        selectedTransformedImage,
        template,
        sceneTransforms,
        setSceneTransforms
    } = useTemplate()
    const [showControls, setShowControls] = useState(false);
    const [activeMode, setActiveMode] = useState<'select' | 'transform'>('select');

    // The selected scene image element (if any) — used to gate + drive zoom.
    // MERGED: a user image layer only exists in the merge, so searching the raw
    // scene found nothing and the bar treated every layer as "not an image".
    const mergedScene = useMergedScene(
        selectedDesign >= 0 ? template?.designs?.[selectedDesign] : undefined,
        selectedDesign,
    );
    const selectedScene = selectedDesign >= 0 ? mergedScene : undefined;
    const selectedImageEl = selectedImageData
        ? findImageElement(selectedScene?.elements, selectedImageData.elementKey)
        : undefined;
    // Zoom only makes sense for a collage cell: a cover-FILL shape-masked image
    // that pans inside a fixed cell (a normal image is resized via handles, not
    // zoomed). pan/zoom is a no-op on FIT or unmasked images, so hide it there.
    const isCollageCell = !!selectedImageEl?.image?.shapeMask && selectedImageEl?.image?.scaleMode !== 'FIT';

    /** Step the selected cell's pan-zoom ±one notch (the +/- buttons). Writes the
     *  same sceneTransforms[id].panZoom the canvas wheel-zoom and export read. */
    const handleZoom = (direction: 1 | -1) => {
        if (!selectedImageEl) return;
        const current = sceneTransforms[selectedImageEl.id]?.panZoom ?? selectedImageEl.image?.pan?.zoom ?? 1;
        const next = stepZoom(current, direction);
        setSceneTransforms((prev: any) => ({...prev, [selectedImageEl.id]: {...prev[selectedImageEl.id], panZoom: next}}));
    };

    // Show controls if there's a selected image
    useEffect(() => {
        setShowControls(!!selectedImageData);

        // Reset to first stage when image selection changes
        if (selectedImageData && !selectedTransformedImage) {
            setActiveMode('select');
        }
    }, [selectedImageData]);

    // Update mode when transform selection changes
    useEffect(() => {
        setActiveMode(selectedTransformedImage ? 'transform' : 'select');
    }, [selectedTransformedImage]);

    const handleRepositionImage = () => {
        setActiveMode('transform');
        // Only trigger transform if not already in transform mode
        if (!selectedTransformedImage && selectedImageData) {
            handleTransformClick(selectedImageData.elementKey);
        }
    };

    if (!showControls) return null;

    return (
        <FloatingImageControlBar
            activeMode={activeMode}
            onClose={handleCloseClick}
            onRepositionImage={handleRepositionImage}
            onResetImage={handleResetClick}
            onApprove={handleCloseClick}
            onZoom={isCollageCell ? handleZoom : undefined}
            selectedDesign={selectedDesign}
        />
    );
};

export default FloatingImageControlBarWrapper;